import clsx from "clsx";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
  DEFAULT_RICH_TEXT_CONFIG,
  isHtmlContentEmpty,
  sanitizeHtml,
} from "../../utils/htmlSanitizer";
import styles from "./RichTextDisplay.module.scss";
import { SanitizedHtml } from "./SanitizedHtml";

export interface RichTextDisplayProps {
  content: string;
  className?: string;
  allowedTags?: string[];
  allowedAttributes?: Record<string, string[]>;
  /** Number of characters to show before truncating (default: 200) */
  truncateLength?: number;
  /** Enable show more/less functionality (default: true) */
  enableShowMore?: boolean;
  /** Start in collapsed state (default: true) */
  defaultCollapsed?: boolean;
  /** Text for the show more button (default: "Show more") */
  showMoreText?: string;
  /** Text for the show less button (default: "Show less") */
  showLessText?: string;
}

export const RichTextDisplay: React.FC<RichTextDisplayProps> = ({
  content,
  className,
  allowedTags,
  allowedAttributes,
  truncateLength = 200,
  enableShowMore = true,
  defaultCollapsed = true,
  showMoreText = "Show more",
  showLessText = "Show less",
}) => {
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [sanitizedContent, setSanitizedContent] = useState("");

  useEffect(() => {
    if (
      !content ||
      typeof content !== "string" ||
      isHtmlContentEmpty(content)
    ) {
      setSanitizedContent("");
      return;
    }

    // Create sanitization config
    const sanitizationConfig = {
      ...DEFAULT_RICH_TEXT_CONFIG,
      ...(allowedTags && { ALLOWED_TAGS: allowedTags }),
      ...(allowedAttributes && { ALLOWED_ATTR: allowedAttributes }),
    };

    // Sanitize the content
    setSanitizedContent(sanitizeHtml(content, sanitizationConfig));
  }, [content, allowedTags, allowedAttributes]);

  // Check if content needs truncation
  useEffect(() => {
    if (!enableShowMore || !contentRef.current) {
      setShouldTruncate(false);
      return;
    }

    // Get the plain text content to check length
    const plainText = contentRef.current.textContent || "";
    setShouldTruncate(plainText.length > truncateLength);
  }, [truncateLength, enableShowMore]);

  if (!sanitizedContent) {
    return null;
  }

  // If show more is disabled, always show full content
  if (!enableShowMore) {
    return (
      <SanitizedHtml
        html={sanitizedContent}
        className={clsx(styles.richTextDisplay, className)}
      />
    );
  }

  return (
    <div className={clsx(styles.richTextDisplay, className)}>
      <SanitizedHtml
        html={sanitizedContent}
        className={clsx(
          styles.content,
          !isExpanded && shouldTruncate && styles.truncated
        )}
      />
      {shouldTruncate && (
        <button
          type="button"
          className={styles.toggleButton}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? showLessText : showMoreText}
        </button>
      )}
    </div>
  );
};
